/* eslint-disable no-await-in-loop */

import { OAuth2Client } from '@kolorado/oauth';
import { AxiosResponse, CancelToken } from 'axios';
import { GetTransactionsCount, GetTransactions, GetExportTransactions } from './types';
import { TransactionsResponse } from '../entities/transactions-v1/transaction-response';
import { OrderByType, SortByType } from '../entities/transactions-v1/query-types';
import { retryWithInterval } from '../globals/utils';
import { axiosInstance } from "../axios-instance";

class Transactions {
  private authClient: OAuth2Client | null;

  private baseUrl: string;

  private storageV1BaseUrl?: string;

  constructor(authClient: OAuth2Client | null, baseUrl: string, storageV1BaseUrl?: string) {
    this.authClient = authClient;
    this.baseUrl = baseUrl;
    this.storageV1BaseUrl = storageV1BaseUrl;
  }

  getCount(input: GetTransactionsCount, cancelToken: CancelToken | undefined): any {
    const accessToken = localStorage.getItem('accessToken');
    // static return for  fake tranasctions
    return axiosInstance.get<number>(`${this.baseUrl}/COUNT`, {
      cancelToken,
      params: input,
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
  }

  getAllDealerTransactions(input: any, cancelToken: CancelToken): Promise<any> {
    const accessToken = localStorage.getItem('accessToken');
    const {
      sortBy = SortByType.gatewayReceiptTime,
      orderBy = OrderByType.DESC,
      limit = 20,
      skip = 0,
    } = input;
    const queryParameters = {
      ...input,
      sortBy: sortBy.toString(),
      orderBy,
      limit,
      skip,
    };
    return axiosInstance.get<TransactionsResponse>(`${this.baseUrl}`, {
      cancelToken,
      params: queryParameters,
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
  }

  getAll(
    input: GetTransactions,
    cancelToken: CancelToken,
  ): Promise<AxiosResponse<TransactionsResponse>> {
    const accessToken = localStorage.getItem('accessToken');
    const {
      sortBy = SortByType.gatewayReceiptTime,
      orderBy = OrderByType.DESC,
      limit = 20,
      skip = 0,
    } = input;
    const queryParameters = {
      ...input,
      sortBy: sortBy.toString(),
      orderBy,
      limit,
      skip,
    };
    return axiosInstance.get<TransactionsResponse>(`${this.baseUrl}`, {
      cancelToken,
      params: queryParameters,
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
  }

  getDetails(input: { id: string; deviceKey: string }): Promise<AxiosResponse<any>> {
    const accessToken = localStorage.getItem('accessToken');
    const { id, deviceKey } = input;
    const queryParameters = { deviceKey };

    return axiosInstance.get<any>(`${this.baseUrl}/${id}`, {
      params: queryParameters,
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
  }

  exportTransactions = async (input: GetExportTransactions) => {
    const accessToken = localStorage.getItem('accessToken');
    const queryParameters = { ...input };
    const response = await axiosInstance.get(`${this.baseUrl}/export`, {
      params: queryParameters,
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
    if (response?.data?.fileId) {
      const urlGenerated = await retryWithInterval(
        this.exportTransactionsStorageApi,
        response.data.fileId,
        60000,
        45,
      );
      return urlGenerated;
    }
    return false;
  };

  downloadDataFileTransaction = (id: string, deviceKey: string) => {
    const accessToken = localStorage.getItem('accessToken');
    return axiosInstance.get(`${this.baseUrl}/${id}/download`, {
      responseType: 'blob',
      params: {
        deviceKey,
      },
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
  };

  downloadVIMSDataFileTransaction = (id: string, telematicsAssetId: string) => {
    const accessToken = localStorage.getItem('accessToken');
    return axiosInstance.get(`${this.baseUrl}/${id}/download`, {
      responseType: 'blob',
      params: {
        telematicsAssetId,
      },
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
  };

  exportTransactionsStorageApi = (input: string) => {
    const accessToken = localStorage.getItem('accessToken');
    const url = `${this.storageV1BaseUrl}/files/${input}/metadata`;
    return axiosInstance.get(`${url}`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    })
      .then((response) => {
        if (response.data.status === 'uploaded') {
          window.location = response.data?.downloadUrl;
          return response.data?.downloadUrl;
        }
        return '';
      })
      .catch((error) => {
        throw new Error(error);
      });
  };
}

export default Transactions;
